import { CDSOption } from '@ciscodesignsystems/cds-react-select';
import { AxiosResponse } from 'axios';
import { isArray, some } from 'lodash';
import { EventChannel, SagaIterator } from 'redux-saga';
import {
  all,
  call,
  delay,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { TabsList } from 'src/app/enums/TabsList';
import {
  ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME,
  FEATURE_FLAG_CONSTANTS,
  MSP_CONSTANTS,
} from 'src/app/utils/constant';
import {
  AddNotification,
  TelemetryErrorAction,
  AddUserToAdminGroup,
  AssignSelectedGroup,
  AssignSelectedGroupForKPA,
  AssignSelectedGroups,
  AssignSelectedGroupsForKPA,
  AssignSelectedInstance,
  AssignSelectedInstanceForKPA,
  AssignSelectedInstances,
  AssignSelectedInstancesForKpaUser,
  AssignSelectedRole,
  AssignSelectedRoles,
  ChangeFirstNameFieldFromEditUserDrawer,
  ChangeLastNameFieldFromEditUserDrawer,
  CreateAllUsers,
  CreateGroup,
  DeleteAssignedGroup,
  DeleteAssignedRole,
  EditUserDetails,
  FetchAllUserRolesAssignments,
  FetchAssignedGroups,
  FetchAssignedRoles,
  FetchEditedUser,
  FetchGroupsToRolesRelations,
  HideEditUserDetailsDrawer,
  HideErrorInviteUsersNotification,
  HideNotification,
  RemoveUserFromAdminGroup,
  SetActiveRemoveGroupId,
  SetActiveRemoveRoleId,
  SetActiveRemoveRoleWithInstanceRowData,
  SetAddUsersStep,
  SetAssignRolesTableDrawer,
  SetAssignRolesWithInstancesTableDrawer,
  SetAssignRolesWithInstancesTableStepper,
  SetGroupsOptionsFromDrawer,
  SetInviteDirty,
  SetInviteStepperPendingToCreateUsers,
  SetInviteUsers,
  SetIsUsersFileUploaded,
  SetProductOptionsFromDrawer,
  SetRegionFilterOptions,
  SetRolesOptionsFromDrawer,
  SetSecurityCloudControlRoleDrawer,
  SetUploadCSVName,
  SetUserIsLoading,
  SetUsersTableActiveTab,
  ShowEditUserDetailsDrawer,
  ShowErrorInviteUsersNotification,
  ShowSuccessCreatedGroupNotification,
  ShowSuccessInviteUsersNotification,
  ToggleAssignGroupsDrawerVisibility,
  ToggleAssignRolesDrawerVisibility,
  ToggleGroupsDrawerVisibility,
  ToggleInviteStepperCreateUsersNotification,
  UploadCSVFile,
  ShowErrorRoleAssignBanner,
  ShowFailureCreatedGroupNotification,
  FetchSharedUserOrgIds,
  FetchSharedGroupOrgIds,
  SetInviteUsersGroupsOptions,
  SetNewGroupName,
  PrepareGroupsOptions,
  ResendUserInvitationEmail,
} from './actions';
import {
  FILE_ERROR,
  FILE_LOADED,
  SHOWS_INVITE_NOTIFICATION_AFTER_MS,
} from './constants';
import {
  IResponseItem,
  createGroupRequestBody,
  getSuccessResponses,
  isCurrentUserPredicate,
  isPendingUserPredicate,
} from './helper';
import {
  EditedUserResponseType,
  IActionWithPayload,
  ICreatedUsers,
  IFileUploadAction,
  IGroupToRoleRelation,
  IGroupWithRoleRelationsResponse,
  IRbacAssignedRole,
  IRbacGroup,
  IUserFormRow,
  OptionsType,
  RbacAssignedGroupsResponseType,
  RbacAssignedRolesResponseType,
  UserInvitationsResponseType,
} from './interfaces';
import { populateRolesSelect } from './populateRolesSelect';
import {
  IOption,
  getActiveEditUserId,
  getActiveRemoveGroupId,
  getActiveRemoveRoleId,
  getActiveRemoveRoleWithInstanceRowData,
  getAssignedGroups,
  getAssignedRolesWithInstancesTableData,
  getDefaultSccRoleFromDrawer,
  getEditUserDetailsDrawerFields,
  getFormFieldsForNewGroup,
  getGroupsOptionsFromDrawer,
  getInviteUsers,
  getOpenedUser,
  getPristineGroupsOptionsFromDrawer,
  getPristineProductOptionsFromDrawer,
  getPristineRolesOptionsFromDrawer,
  getSccRoleFromDrawer,
  getSelectedGroupsIDs,
  getSelectedGroupsOptionsFromDrawer,
  getSelectedInstancesIDsInDrawer,
  getSelectedRolesIDs,
  getSelectedRolesIDsInAssignRolesDrawer,
  getSelectedRolesIDsWithInstances,
  getUserDetails,
  getUserFromEnterprise,
  getIsKPA,
  getNewGroupName,
  getGroupsOptions,
} from './selectors';
import {
  UserTelemetryKeys,
  TRUE_VALUE,
  UNKNOWN_ERROR,
  UserTelemetryValues,
  NETWORK_ERROR,
} from './telemetryEvents/usersTelemetryConstants';
import { usersErrorTelemetry } from './telemetryEvents/usersTelemetryEvents';
import {
  createFileReaderChannel,
  selectNewlyCreatedGroupOption,
  transformGroupsToGroupsOptions,
  transformInviteUsers,
  validateUploadChange,
} from './utilities';
import { isSccEnterpriseAdmin, prometheusAPIClient } from '../../api/clients';
import { EditUserNotificationType } from '../../enums/EditUser';
import { RolesPermission } from '../../enums/RolesPermissionList';
import { IBootstrap, IProductInstances, IUser } from '../../interfaces/ICommon';
import { FileReaderEventActionType } from '../../interfaces/IFileReaderEventActions';
import { IProductTransformed } from '../../interfaces/IProductList';
import { IRole } from '../../interfaces/IUserRoles';
import {
  IAssignedRoleWIthInstanceTableRow,
  IEditUserDetailsDrawerFields,
  INewGroupFields,
  IRoleAndInstanceIds,
  IRoleAndTenantIds,
  ISharedUsersOrgIds,
  ISharedGroupsOrgIds,
} from '../../interfaces/IUsers';
import * as Paths from '../../paths';
import { notifyFormPopulated } from '../../services/formPopulated';
import {
  extractGroupsOptions,
  filterGroups,
  mapPreviouslySelectedOptions,
} from '../../utils/filter-options';
import { getInstanceIdByRoleId } from '../../utils/shared';
import {
  FetchAllGroups,
  FetchAllProductsInstances,
  FetchAllRoles,
  FetchAllUsers,
} from '../common/actions';
import { handleFetchAllUsers } from '../common/saga';
import {
  getAllProductsInstances,
  getAllGroups,
  getAllUsers,
  getBootstrapConfig,
  getNavigation,
  getUniqueRegions,
  getAllRoles,
  getFeatureFlagsSet,
} from '../common/selectors';
import {
  getEnterpriseDetails,
  getEnterpriseId,
} from '../enterprises/selectors';
import { FETCH_PRODUCTS_LIST_SUCCESS } from '../overview/store/constants';

type FetchSharedUsersOrgIdsResponseType =
  | AxiosResponse<ISharedUsersOrgIds>
  | any;

type FetchSharedGroupOrgIdsResponseType =
  | AxiosResponse<ISharedGroupsOrgIds>
  | any;

interface ITelemetryEvent {
  name: string;
  properties: Record<string, any>;
}

export function* uploadCSVFile(action: IFileUploadAction): SagaIterator {
  const { info, setFileError } = action.payload;

  const uploadChangeError = validateUploadChange(info);
  if (uploadChangeError) {
    setFileError(uploadChangeError);
    return;
  }

  yield put(SetUserIsLoading(true));
  yield put(SetUploadCSVName(info.file.name));

  const file = info.file.originFileObj as File;

  const fileReaderChannel: EventChannel<FileReaderEventActionType> = yield call(
    () => createFileReaderChannel(file)
  );

  try {
    let eventAction: FileReaderEventActionType;

    while ((eventAction = yield take(fileReaderChannel))) {
      if (
        eventAction.type === FILE_ERROR &&
        typeof eventAction.payload === 'string'
      ) {
        setFileError(eventAction.payload);
        return;
      } else if (
        eventAction.type === FILE_LOADED &&
        typeof eventAction.payload !== 'string' &&
        typeof eventAction.payload !== 'number' &&
        eventAction.payload
      ) {
        yield put(SetInviteUsers(eventAction.payload));
        yield put(SetUserIsLoading(false));
        yield put(SetIsUsersFileUploaded(true));
      } else {
        yield put(eventAction);
      }
    }
  } finally {
    fileReaderChannel.close();
  }
}

function buildAssignRoleErrorTelemetry(
  id: string,
  error: any
): ITelemetryEvent {
  return {
    name: usersErrorTelemetry.userRowActionMenu.assignRoleError.name,
    properties: {
      ...Object.fromEntries(
        usersErrorTelemetry.userRowActionMenu.assignRoleError.properties ??
          new Map<UserTelemetryKeys, UserTelemetryValues>()
      ),
      [UserTelemetryKeys.TargetObjectId]: id,
      [UserTelemetryKeys.ErrorEvent]: TRUE_VALUE,
      [UserTelemetryKeys.ErrorCode]: error?.response?.status ?? NETWORK_ERROR,
      [UserTelemetryKeys.ErrorReason]:
        error instanceof Error ? error.message : UNKNOWN_ERROR,
    },
  };
}

export function* handleFinishInviteStepper(
  action: IActionWithPayload<boolean>
): Generator {
  const tenantFeatureFlag = action.payload;

  const { basePath, isStandalone } = (yield select(
    getBootstrapConfig
  )) as IBootstrap;
  const { navigateFn } = (yield select(getNavigation)) as any;
  const { usersAlreadyExistInEnterprise, usersNotExistInEnterprise }: any =
    yield select(getUserFromEnterprise);
  const isUserKPA = yield select(getIsKPA);

  if (usersAlreadyExistInEnterprise.length && isUserKPA) {
    yield put(AssignSelectedInstancesForKpaUser.PENDING());
    yield put(AssignSelectedGroupsForKPA.PENDING());
  }

  const path = `${isStandalone ? '' : basePath}/${Paths.ACCESS_CONTROL}/${Paths.USERS}`;

  yield call(navigateFn, path);
  yield put(SetInviteDirty(false));
  yield put(SetAddUsersStep(1));

  if (usersNotExistInEnterprise.length > 0) {
    yield put(CreateAllUsers.PENDING(tenantFeatureFlag));
  }

  yield delay(SHOWS_INVITE_NOTIFICATION_AFTER_MS);
}

export function* createAllUsers(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const inviteUsers = (yield select(getInviteUsers)) as IUserFormRow[];
  const selectedGroupsIDs = (yield select(getSelectedGroupsIDs)) as string[];
  const selectedRolesIDs = (yield select(getSelectedRolesIDs)) as string[];
  const allRoles = (yield select(getAllRoles)) as IRole[];
  const allInstances = (yield select(
    getAllProductsInstances
  )) as IProductInstances;

  const { usersNotExistInEnterprise }: any = yield select(
    getUserFromEnterprise
  );
  const isUserKPA = yield select(getIsKPA);
  // Hide banner with previous error before new invite
  yield put(HideErrorInviteUsersNotification());

  const selectedRolesIDsWithInstances = selectedRolesIDs.map(
    (selectedRolesID) => ({
      roleId: selectedRolesID,
      tenantId: getInstanceIdByRoleId(
        selectedRolesID,
        allRoles,
        allInstances.items
      ),
    })
  );

  const isAdminSccRole = selectedRolesIDsWithInstances.some(
    (obj) => obj.roleId === RolesPermission.ADMINISTRATOR
  );

  const users =
    isUserKPA && usersNotExistInEnterprise.length
      ? usersNotExistInEnterprise
      : inviteUsers;
  const usersDetailsPayload = transformInviteUsers(users, isAdminSccRole);
  try {
    const response: UserInvitationsResponseType =
      yield prometheusAPIClient.post(
        `/api/enterprises/${enterpriseId}/userInvitations`,
        {
          users: usersDetailsPayload,
          groupIds: selectedGroupsIDs,
          roles: selectedRolesIDsWithInstances,
        }
      );

    yield put(CreateAllUsers.SUCCESS(response.data));
    // Re-fetch all Users for update information in tabs.
    yield call(handleFetchAllUsers);

    const { errors: invitedUsersErrors, invitedUsersCount } = response.data;
    if (isArray(invitedUsersErrors) && invitedUsersErrors.length) {
      yield put(ShowErrorInviteUsersNotification());
    }
    if (invitedUsersCount) {
      yield put(ShowSuccessInviteUsersNotification());
      yield put(
        SetAssignRolesWithInstancesTableStepper([
          { selectedRole: null, rolesOptions: [], instancesOptions: [] },
        ])
      );

      const refreshedUsers = (yield select(getAllUsers)) as IUser[];
      if (isArray(refreshedUsers)) {
        if (some(refreshedUsers, isPendingUserPredicate)) {
          // Move to tab "Pending Users" in case when we have Users there
          // Also show Notification for that tab
          yield put(SetUsersTableActiveTab(TabsList.PendingUsers));
          yield put(ToggleInviteStepperCreateUsersNotification(true));
        } else if (some(refreshedUsers, isCurrentUserPredicate)) {
          // Move to tab "Current Users" in case when we do not have Pending Users
          yield put(SetUsersTableActiveTab(TabsList.CurrentUsers));
        }
      }
    }
  } catch (error: any) {
    yield put(
      CreateAllUsers.ERROR({
        ...(error as ICreatedUsers),
        errorReason: error.message || UNKNOWN_ERROR,
      })
    );
    yield put(ShowErrorInviteUsersNotification());
  }
}

export function* prepareGroupsOptions(): Generator {
  yield take(FetchAllGroups.Success.TYPE);
  const allGroups = (yield select(getAllGroups)) as IRbacGroup[];
  const newGroupName = (yield select(getNewGroupName)) as string;

  // setting groups options
  const groupsOptions = transformGroupsToGroupsOptions(allGroups);

  // selecting the new group inside group options dropdown
  if (newGroupName) {
    const currentGroupsOptions = (yield select(getGroupsOptions)) as IOption[];
    yield put(
      SetInviteUsersGroupsOptions(
        selectNewlyCreatedGroupOption(
          groupsOptions,
          currentGroupsOptions,
          newGroupName
        )
      )
    );
    yield put(SetNewGroupName(''));
  } else {
    yield put(SetInviteUsersGroupsOptions(groupsOptions));
  }
}

export function* createGroup(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { name, description, appliesTo } = (yield select(
    getFormFieldsForNewGroup
  )) as INewGroupFields;
  const enterpriseDetails = yield select(getEnterpriseDetails);
  const featureFlagsSet = (yield select(getFeatureFlagsSet)) as Set<string>;
  const hasMspRestrictSharedGroupAccess = featureFlagsSet.has(
    FEATURE_FLAG_CONSTANTS?.MSP_RESTRICT_SHARED_GROUP_ACCESS
  );
  const enterpriseType = (
    enterpriseDetails as { details?: { enterpriseType?: string } }
  )?.details?.enterpriseType;
  const isManagerOrg =
    hasMspRestrictSharedGroupAccess &&
    (enterpriseType === MSP_CONSTANTS.multiOrgManager ||
      enterpriseType === MSP_CONSTANTS.mspManager);

  const requestBody = createGroupRequestBody(
    name.value,
    description.value,
    appliesTo?.value,
    isManagerOrg
  );

  try {
    yield prometheusAPIClient.post(
      `/api/enterprises/${enterpriseId}/rbac/groups`,
      requestBody
    );

    yield put(FetchAllGroups.PENDING());
    yield put(ShowSuccessCreatedGroupNotification());
    yield put(ToggleGroupsDrawerVisibility(false));

    yield put(CreateGroup.SUCCESS());
  } catch (error) {
    if (error instanceof Error) {
      yield put(CreateGroup.ERROR({ error, errorReason: error.message }));
    } else {
      yield put(
        CreateGroup.ERROR({
          error: new Error(UNKNOWN_ERROR),
          errorReason: UNKNOWN_ERROR,
        })
      );
    }

    yield put(ShowFailureCreatedGroupNotification());
  }
}

export function* fetchAssignedGroupsSucess(): Generator {
  const allGroups = (yield select(getAllGroups)) as IRbacGroup[];
  const assignedGroups = (yield select(getAssignedGroups)) as IRbacGroup[];
  const groupsOptionsFromDrawer = (yield select(
    getGroupsOptionsFromDrawer
  )) as CDSOption[];

  const filteredGroups = filterGroups(allGroups, assignedGroups);
  const extractedOptions = extractGroupsOptions(filteredGroups);
  // to check if there were selected options before refresh
  const options = mapPreviouslySelectedOptions(
    extractedOptions,
    groupsOptionsFromDrawer
  );
  yield put(SetGroupsOptionsFromDrawer(options));
}

export function* fetchAssignedRoles(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { id } = (yield select(getOpenedUser)) as IUser;

  try {
    const response: RbacAssignedRolesResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/users/${id}/roles`
      );
    const data = response.data.rbacUserRolesAssignments;

    yield put(FetchAssignedRoles.SUCCESS(data));
  } catch (error) {
    yield put(FetchAssignedRoles.ERROR(error as Error));
  }
}

export function* fetchAssignedGroups(
  action: IActionWithPayload<boolean>
): Generator {
  const { payload: shouldFetchRoles = true } = action;

  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { id } = (yield select(getOpenedUser)) as IUser;

  try {
    const response: RbacAssignedGroupsResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/users/${id}/groups`
      );

    const data = response.data.rbacUserGroupsAssignments;

    yield put(FetchAssignedGroups.SUCCESS(data));

    if (shouldFetchRoles) {
      yield put(FetchGroupsToRolesRelations.PENDING());
    }
  } catch (error) {
    yield put(FetchAssignedGroups.ERROR(error as Error));
  }
}

export function* toggleAssignRolesDrawerVisibility(
  action: IActionWithPayload<boolean>
): Generator {
  const isDrawerVisible = action.payload;
  const defaultSccRoleFromDrawer = yield select(getDefaultSccRoleFromDrawer);
  const userDetails = yield select(getUserDetails);

  if (isDrawerVisible && userDetails) {
    yield put(SetSecurityCloudControlRoleDrawer(defaultSccRoleFromDrawer));
  } else {
    yield take(HideNotification.TYPE);
    yield put(
      SetAssignRolesTableDrawer([
        {
          selectedProduct: null,
          productsOptions: [],
          rolesOptions: {},
        },
      ])
    );
    yield put(
      SetAssignRolesWithInstancesTableDrawer([
        {
          selectedRole: null,
          rolesOptions: [],
          instancesOptions: {},
        },
      ])
    );
  }
}

export function* assignRolesCallbackFn(): Generator {
  yield put(AssignSelectedRoles.SUCCESS());
  yield put(AssignSelectedInstances.SUCCESS());

  yield put(AddNotification(EditUserNotificationType.ASSIGN_ROLES));
  yield put(ToggleAssignRolesDrawerVisibility(false));
  // -- TODO: Add notification

  yield refreshEditUserPage('roles');
}

export function* assignRolesCallbackFnForKPA(): Generator {
  const { usersAlreadyExistInEnterprise } = (yield select(
    getUserFromEnterprise
  )) as { usersAlreadyExistInEnterprise: IUser[] };
  const userIds = usersAlreadyExistInEnterprise.map((user: any) => user.id);

  for (const id of userIds) {
    yield put(FetchAllUserRolesAssignments.PENDING(id));
  }

  yield put(AssignSelectedInstancesForKpaUser.SUCCESS());
  yield put(ShowSuccessInviteUsersNotification());
}

export function* assignSelectedRoles(): Generator {
  const rolesIDs: OptionsType = yield select(
    getSelectedRolesIDsInAssignRolesDrawer
  );
  const sccRole = (yield select(getSccRoleFromDrawer)) as RolesPermission;
  const userDetails = (yield select(getUserDetails)) as IUser;

  if (userDetails.isAdmin && sccRole === RolesPermission.MEMBER) {
    yield put(RemoveUserFromAdminGroup.PENDING());
  }
  if (!userDetails.isAdmin && sccRole === RolesPermission.ADMINISTRATOR) {
    yield put(AddUserToAdminGroup.PENDING());
  }

  if (!rolesIDs.length) {
    yield assignRolesCallbackFn();
  }

  try {
    yield all(
      rolesIDs.map((id: string, index: number) => {
        const callbackFnArg =
          index === rolesIDs.length - 1 ? assignRolesCallbackFn : null;
        return put(
          AssignSelectedRole.PENDING({ id, callbackFn: callbackFnArg })
        );
      })
    );
  } catch (error) {
    yield put(AssignSelectedRoles.ERROR(error as Error));
    yield put(AddNotification(EditUserNotificationType.ERROR));
  }
}

export function* assignSelectedInstances(): Generator {
  const instancesIDs: OptionsType = yield select(
    getSelectedInstancesIDsInDrawer
  );
  const sccRole = (yield select(getSccRoleFromDrawer)) as RolesPermission;
  const userDetails = (yield select(getUserDetails)) as IUser;

  if (userDetails.isAdmin && sccRole === RolesPermission.MEMBER) {
    yield put(RemoveUserFromAdminGroup.PENDING());
  }
  if (!userDetails.isAdmin && sccRole === RolesPermission.ADMINISTRATOR) {
    yield put(AddUserToAdminGroup.PENDING());
  }

  if (!instancesIDs.length) {
    yield assignRolesCallbackFn();
  }

  try {
    yield all(
      instancesIDs.map(
        (roleAndInstanceIds: IRoleAndInstanceIds, index: number) => {
          const callbackFnArg =
            index === instancesIDs.length - 1 ? assignRolesCallbackFn : null;
          return put(
            AssignSelectedInstance.PENDING({
              roleAndInstanceIds,
              callbackFn: callbackFnArg,
            })
          );
        }
      )
    );
  } catch (error) {
    yield put(AssignSelectedInstances.ERROR(error as Error));
    yield put(AddNotification(EditUserNotificationType.ERROR));
  }
}

export function* assignSelectedInstance(
  action: IActionWithPayload<{
    roleAndInstanceIds: IRoleAndInstanceIds;
    callbackFn: Function | null;
  }>
): Generator {
  const {
    payload: { roleAndInstanceIds, callbackFn },
  } = action;
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { id } = (yield select(getOpenedUser)) as IUser;

  try {
    if (
      roleAndInstanceIds.instanceId ===
      ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME
    ) {
      yield put(AddUserToAdminGroup.PENDING());
      yield put(
        SetSecurityCloudControlRoleDrawer(
          ENTERPRISE_PRODUCT_ADMINISTRATOR_ROLE_NAME
        )
      );
    } else {
      yield prometheusAPIClient.post(
        `/api/enterprises/${enterpriseId}/rbac/users/${id}/roles`,
        {
          roleId: roleAndInstanceIds.roleId,
          tenantId: roleAndInstanceIds.instanceId,
          enterpriseId,
          userId: id,
        }
      );
    }
    yield put(AssignSelectedInstance.SUCCESS());

    // -- is last item and has callbackFn
    if (callbackFn) {
      yield callbackFn();
    }
  } catch (error) {
    yield put(TelemetryErrorAction(buildAssignRoleErrorTelemetry(id, error)));
    yield put(AssignSelectedInstance.ERROR(error as Error));
    yield put(ShowErrorRoleAssignBanner());
    yield put(ToggleAssignRolesDrawerVisibility(false));
    yield refreshEditUserPage('roles');
  }
}

export function* assignSelectedInstancesForKpaUser(): Generator {
  const selectedRolesIDsWithInstances: OptionsType = yield select(
    getSelectedRolesIDsWithInstances
  );
  const { usersAlreadyExistInEnterprise } = (yield select(
    getUserFromEnterprise
  )) as { usersAlreadyExistInEnterprise: IUser[] };

  if (!selectedRolesIDsWithInstances) {
    yield assignRolesCallbackFnForKPA();
  }

  try {
    const actions = usersAlreadyExistInEnterprise.map((user: any) => {
      return selectedRolesIDsWithInstances.map(
        (roleAndInstanceIds: IRoleAndInstanceIds, index: number) => {
          const profileId = user?.id;

          const callbackFnArg =
            index === selectedRolesIDsWithInstances.length - 1
              ? assignRolesCallbackFnForKPA
              : null;
          return put(
            AssignSelectedInstanceForKPA.PENDING({
              roleAndInstanceIds,
              callbackFn: callbackFnArg,
              profileId,
            })
          );
        }
      );
    });
    yield all(actions.flat());
  } catch (error) {
    yield put(AssignSelectedInstancesForKpaUser.ERROR(error as Error));
  }
}

export function* assignSelectedInstanceForKPA(
  action: IActionWithPayload<{
    roleAndInstanceIds: IRoleAndTenantIds;
    callbackFn: Function | null;
    profileId: string;
  }>
): Generator {
  const {
    payload: { roleAndInstanceIds, callbackFn, profileId },
  } = action;
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  try {
    yield prometheusAPIClient.post(
      `/api/enterprises/${enterpriseId}/rbac/users/${profileId}/roles`,
      {
        roleId: roleAndInstanceIds.roleId,
        tenantId: roleAndInstanceIds.tenantId,
        enterpriseId,
        userId: profileId,
      }
    );
    yield put(AssignSelectedInstance.SUCCESS());

    if (callbackFn) {
      yield callbackFn();
    }
  } catch (error) {
    yield put(AssignSelectedInstance.ERROR(error as Error));
  }
}

export function* assignSelectedRole(
  action: IActionWithPayload<{
    id: string;
    callbackFn: Function | null;
  }>
): Generator {
  const {
    payload: { id: roleId, callbackFn },
  } = action;
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { id } = (yield select(getOpenedUser)) as IUser;
  const allRoles = (yield select(getAllRoles)) as IRole[];
  const allInstances = (yield select(
    getAllProductsInstances
  )) as IProductInstances;

  const tenantId = getInstanceIdByRoleId(roleId, allRoles, allInstances.items);

  try {
    if (roleId === RolesPermission.ADMINISTRATOR && isSccEnterpriseAdmin()) {
      yield put(AddUserToAdminGroup.PENDING());
      yield put(
        SetSecurityCloudControlRoleDrawer(RolesPermission.ADMINISTRATOR)
      );
    } else {
      yield prometheusAPIClient.post(
        `/api/enterprises/${enterpriseId}/rbac/users/${id}/roles`,
        {
          roleId,
          tenantId,
          enterpriseId,
          userId: id,
        }
      );
    }
    yield put(AssignSelectedRole.SUCCESS());

    // -- is last item and has callbackFn
    if (callbackFn) {
      yield callbackFn();
    }
  } catch (error) {
    yield put(TelemetryErrorAction(buildAssignRoleErrorTelemetry(id, error)));
    yield put(AssignSelectedRole.ERROR(error as Error));
    yield put(ShowErrorRoleAssignBanner());
    yield put(ToggleAssignRolesDrawerVisibility(false));
    yield refreshEditUserPage('roles');
  }
}

export function* addUserToAdminGroup(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { id } = (yield select(getUserDetails)) as IUser;

  try {
    yield prometheusAPIClient.post(`/api/enterprises/${enterpriseId}/admins`, {
      userId: id,
    });
    yield put(AddUserToAdminGroup.SUCCESS());
    yield refreshEditUserPage('user');
  } catch (error) {
    yield put(TelemetryErrorAction(buildAssignRoleErrorTelemetry(id, error)));
    yield put(AddUserToAdminGroup.ERROR(error as Error));
  }
}

export function* removeUserToAdminGroup(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { id } = (yield select(getUserDetails)) as IUser;

  try {
    yield prometheusAPIClient.delete(
      `/api/enterprises/${enterpriseId}/admins/${id}`
    );
    yield put(RemoveUserFromAdminGroup.SUCCESS());
    yield put(AddNotification(EditUserNotificationType.REMOVE_FROM_ROLE));
    yield refreshEditUserPage('user');
    yield refreshEditUserPage('roles');
  } catch (error) {
    yield put(RemoveUserFromAdminGroup.ERROR(error as Error));
  }
}

export function* assignGroupsCallbackFn(): Generator {
  yield put(AssignSelectedGroups.SUCCESS());

  yield put(AddNotification(EditUserNotificationType.ASSIGN_TO_GROUPS));
  yield put(ToggleAssignGroupsDrawerVisibility(false));
  // -- TODO: Add notification

  yield refreshEditUserPage('groups');
}

export function* assignSelectedGroups(): Generator {
  const selectedGroupsOptionsFromDrawer: OptionsType = yield select(
    getSelectedGroupsOptionsFromDrawer
  );

  const groupIDs = selectedGroupsOptionsFromDrawer.map(
    (option: IOption) => option.value
  );

  try {
    yield all(
      groupIDs.map((id: string, index: number) => {
        const callbackFnArg =
          index === groupIDs.length - 1 ? assignGroupsCallbackFn : null;
        return put(
          AssignSelectedGroup.PENDING({ id, callbackFn: callbackFnArg })
        );
      })
    );
  } catch (error) {
    yield put(AssignSelectedGroups.ERROR(error as Error));
    yield put(AddNotification(EditUserNotificationType.ERROR));
  }
}

export function* assignSelectedGroup(
  action: IActionWithPayload<{
    id: string;
    callbackFn: Function | null;
  }>
): Generator {
  const {
    payload: { id: groupId, callbackFn },
  } = action;
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const { id } = (yield select(getOpenedUser)) as IUser;

  try {
    yield prometheusAPIClient.post(
      `/api/enterprises/${enterpriseId}/rbac/users/${id}/groups`,
      {
        groupId,
        enterpriseId,
        userId: id,
      }
    );

    yield put(AssignSelectedGroup.SUCCESS());

    // -- is last item and has callbackFn
    if (callbackFn) {
      yield callbackFn();
    }
  } catch (error) {
    const assignGroupErrorName =
      usersErrorTelemetry.userRowActionMenu.assignGroupError.name;
    const assignGroupErrorProperties = {
      ...Object.fromEntries(
        usersErrorTelemetry.userRowActionMenu.assignGroupError.properties ??
          new Map<UserTelemetryKeys, UserTelemetryValues>()
      ),
      [UserTelemetryKeys.TargetObjectId]: id,
      [UserTelemetryKeys.ErrorEvent]: TRUE_VALUE,
      [UserTelemetryKeys.ErrorCode]:
        (error as any)?.response?.status ?? NETWORK_ERROR,
      [UserTelemetryKeys.ErrorReason]:
        error instanceof Error ? error.message : UNKNOWN_ERROR,
    };
    yield put(
      TelemetryErrorAction({
        name: assignGroupErrorName,
        properties: assignGroupErrorProperties,
      })
    );

    yield put(AssignSelectedGroup.ERROR(error as Error));
  }
}

export function* assignGroupsCallbackFnForKPA(): Generator {
  const { usersAlreadyExistInEnterprise } = (yield select(
    getUserFromEnterprise
  )) as { usersAlreadyExistInEnterprise: IUser[] };

  const userIds = usersAlreadyExistInEnterprise.map((user: any) => user.id);

  for (const id of userIds) {
    yield put(FetchAllUserRolesAssignments.PENDING(id));
  }

  yield put(AssignSelectedGroupsForKPA.SUCCESS());
  yield put(ShowSuccessInviteUsersNotification());
}

export function* assignSelectedGroupsForKPA(): Generator {
  const selectedGroupsIDs = (yield select(getSelectedGroupsIDs)) as string[];

  const { usersAlreadyExistInEnterprise }: any = yield select(
    getUserFromEnterprise
  );

  try {
    const actions = usersAlreadyExistInEnterprise.map((user: any) => {
      return selectedGroupsIDs.map((groupId: string, index: number) => {
        const profileId = user?.id;

        const callbackFnArg =
          index === selectedGroupsIDs.length - 1
            ? assignGroupsCallbackFnForKPA
            : null;

        return put(
          AssignSelectedGroupForKPA.PENDING({
            id: groupId,
            callbackFn: callbackFnArg,
            profileId,
          })
        );
      });
    });

    yield all(actions.flat());
  } catch (error) {
    yield put(AssignSelectedGroups.ERROR(error as Error));
    yield put(AddNotification(EditUserNotificationType.ERROR));
  }
}

export function* assignSelectedGroupForKPA(
  action: IActionWithPayload<{
    id: string;
    callbackFn: Function | null;
    profileId: string;
  }>
): Generator {
  const {
    payload: { id: groupId, callbackFn, profileId },
  } = action || {};
  const enterpriseId = (yield select(getEnterpriseId)) as string;

  try {
    yield prometheusAPIClient.post(
      `/api/enterprises/${enterpriseId}/rbac/users/${profileId}/groups`,
      {
        groupId,
        enterpriseId,
        userId: profileId,
      }
    );

    yield put(AssignSelectedGroup.SUCCESS());

    if (callbackFn) {
      yield callbackFn();
    }
  } catch (error) {
    yield put(AssignSelectedGroup.ERROR(error as Error));
  }
}

export function* deleteAssignedRole(
  action: IActionWithPayload<boolean>
): Generator {
  const hasTenantFeatureFlag = action.payload;
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const userId = yield select(getActiveEditUserId);
  const roleId = yield select(getActiveRemoveRoleId);

  const activeRemoveInstance = (yield select(
    getActiveRemoveRoleWithInstanceRowData
  )) as IAssignedRoleWIthInstanceTableRow | null;

  try {
    yield prometheusAPIClient.delete(
      `/api/enterprises/${enterpriseId}/rbac/users/${userId}/roles/${
        hasTenantFeatureFlag ? activeRemoveInstance?.id : roleId
      }`
    );

    yield put(DeleteAssignedRole.SUCCESS());
    yield put(AddNotification(EditUserNotificationType.REMOVE_FROM_ROLE));

    yield refreshEditUserPage('roles');
  } catch (error) {
    const removeRoleErrorName =
      usersErrorTelemetry.userRowActionMenu.removeRoleError.name;
    const removeRoleErrorProperties = {
      ...Object.fromEntries(
        usersErrorTelemetry.userRowActionMenu.removeRoleError.properties ??
          new Map<UserTelemetryKeys, UserTelemetryValues>()
      ),
      [UserTelemetryKeys.TargetObjectId]: userId,
      [UserTelemetryKeys.ErrorEvent]: TRUE_VALUE,
      [UserTelemetryKeys.ErrorCode]:
        (error as any)?.response?.status ?? NETWORK_ERROR,
      [UserTelemetryKeys.ErrorReason]:
        error instanceof Error ? error.message : UNKNOWN_ERROR,
    };
    yield put(
      TelemetryErrorAction({
        name: removeRoleErrorName,
        properties: removeRoleErrorProperties,
      })
    );

    yield put(
      DeleteAssignedRole.ERROR(
        error instanceof Error ? error.message : UNKNOWN_ERROR
      )
    );
  }
}

export function* deleteAssignedGroup(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const userId = yield select(getActiveEditUserId);
  const groupId = yield select(getActiveRemoveGroupId);

  try {
    yield prometheusAPIClient.delete(
      `/api/enterprises/${enterpriseId}/rbac/users/${userId}/groups/${groupId}`
    );

    yield put(DeleteAssignedGroup.SUCCESS());
    yield put(AddNotification(EditUserNotificationType.REMOVE_FROM_GROUP));

    yield refreshEditUserPage('groups');
  } catch (error) {
    const removeGroupErrorName =
      usersErrorTelemetry.userRowActionMenu.removeGroupError.name;
    const removeGroupErrorProperties = {
      ...Object.fromEntries(
        usersErrorTelemetry.userRowActionMenu.removeGroupError.properties ??
          new Map<UserTelemetryKeys, UserTelemetryValues>()
      ),
      [UserTelemetryKeys.TargetObjectId]: userId,
      [UserTelemetryKeys.ErrorEvent]: TRUE_VALUE,
      [UserTelemetryKeys.ErrorCode]:
        (error as any)?.response?.status ?? NETWORK_ERROR,
      [UserTelemetryKeys.ErrorReason]:
        error instanceof Error ? error.message : UNKNOWN_ERROR,
    };
    yield put(
      TelemetryErrorAction({
        name: removeGroupErrorName,
        properties: removeGroupErrorProperties,
      })
    );
    yield put(DeleteAssignedGroup.ERROR(error as Error));
  }
}

export function* handleFetchProductsListSuccess(
  action: IActionWithPayload<{ allProducts: IProductTransformed[] }>
): Generator {
  // -- TODO: Remove when necessary products for enterprise are created on backend
  const productOptions = [
    { label: 'Firewall', value: 'Firewall' },
    { label: 'XDR', value: 'XDR' },
    { label: 'DUO', value: 'DUO' },
    { label: 'Endpoint', value: 'Endpoint' },
  ];

  yield put(SetProductOptionsFromDrawer(productOptions));
}

export function* clearDrawers(target: string): Generator {
  const pristineProductOptionsFromDrawer = yield select(
    getPristineProductOptionsFromDrawer
  );
  const pristineRolesOptionsFromDrawer = yield select(
    getPristineRolesOptionsFromDrawer
  );
  const pristineGroupsOptionsFromDrawer = yield select(
    getPristineGroupsOptionsFromDrawer
  );

  if (target === 'roles') {
    yield put(ToggleAssignRolesDrawerVisibility(false));
    yield put(SetProductOptionsFromDrawer(pristineProductOptionsFromDrawer));
    yield put(SetRolesOptionsFromDrawer(pristineRolesOptionsFromDrawer));
  }

  if (target === 'groups') {
    yield put(ToggleAssignGroupsDrawerVisibility(false));
    yield put(SetGroupsOptionsFromDrawer(pristineGroupsOptionsFromDrawer));
  }
}

export function* refreshEditUserPage(target: string): Generator {
  if (target === 'user') {
    const { id } = (yield select(getUserDetails)) as IUser;
    yield put(FetchSharedUserOrgIds.PENDING());
    yield put(FetchEditedUser.PENDING(id));
  }
  if (target === 'roles') {
    yield put(FetchAllRoles.PENDING());
    yield put(FetchAssignedRoles.PENDING());

    yield take(HideNotification.TYPE);
    yield put(SetActiveRemoveRoleId(null));
    yield put(SetActiveRemoveRoleWithInstanceRowData(null));
    yield clearDrawers('roles');
  }

  if (target === 'groups') {
    yield put(FetchAllGroups.PENDING());
    yield put(FetchAssignedGroups.PENDING());
    yield put(FetchSharedGroupOrgIds.PENDING());

    yield take(HideNotification.TYPE);
    yield put(SetActiveRemoveGroupId(null));
    yield clearDrawers('groups');
  }
}

export function* fetchEditedUser(
  action: IActionWithPayload<string>
): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const currentUserId = action.payload;
  try {
    const response: EditedUserResponseType = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/users/${currentUserId}`
    );
    yield put(FetchEditedUser.SUCCESS(response.data));
  } catch (error) {
    yield put(FetchEditedUser.ERROR(error as Error));
    yield put(AddNotification(EditUserNotificationType.ERROR));
  }
}

export function* handleShowEditUserDetailsDrawer(): Generator {
  const { profile } = (yield select(getUserDetails)) as IUser;
  yield put(
    ChangeFirstNameFieldFromEditUserDrawer({
      value: profile.firstName,
      error: false,
      touched: false,
    })
  );
  yield put(
    ChangeLastNameFieldFromEditUserDrawer({
      value: profile.lastName,
      error: false,
      touched: false,
    })
  );
}

export function* editUserDetails(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const userDetails = (yield select(getUserDetails)) as IUser;
  const { firstName, lastName } = (yield select(
    getEditUserDetailsDrawerFields
  )) as IEditUserDetailsDrawerFields;
  try {
    yield prometheusAPIClient.put(
      `/api/enterprises/${enterpriseId}/users/${userDetails.id}`,
      {
        ...userDetails.profile,
        firstName: firstName.value,
        lastName: lastName.value,
      }
    );

    yield put(EditUserDetails.SUCCESS());
    yield put(FetchEditedUser.PENDING(userDetails.id));
    yield put(HideEditUserDetailsDrawer());
    yield put(AddNotification(EditUserNotificationType.EDIT_NAME));
  } catch (error) {
    const editUserNameErrorName =
      usersErrorTelemetry.editUser.editUserNameError.name;
    const editUserNameErrorProperties = {
      ...Object.fromEntries(
        usersErrorTelemetry.editUser.editUserNameError.properties ??
          new Map<UserTelemetryKeys, UserTelemetryValues>()
      ),
      [UserTelemetryKeys.TargetObjectId]: userDetails.id,
      [UserTelemetryKeys.ErrorEvent]: TRUE_VALUE,
      [UserTelemetryKeys.ErrorCode]:
        (error as any)?.response?.status ?? NETWORK_ERROR,
      [UserTelemetryKeys.ErrorReason]:
        error instanceof Error ? error.message : UNKNOWN_ERROR,
    };
    yield put(
      TelemetryErrorAction({
        name: editUserNameErrorName,
        properties: editUserNameErrorProperties,
      })
    );
    yield put(EditUserDetails.ERROR(error as Error));
    yield put(AddNotification(EditUserNotificationType.ERROR));
  }
}

export function* getSharedUserOrgIds(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const userDetails = (yield select(getUserDetails)) as IUser;
  if (!enterpriseId || !userDetails) return;
  try {
    const resp: FetchSharedUsersOrgIdsResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/sharedUsers/${userDetails?.id}`
      );

    yield put(FetchSharedUserOrgIds.SUCCESS(resp.data));
  } catch (error) {
    yield put(FetchSharedUserOrgIds.ERROR(error as Error));
  }
}

export function* getSharedGroupOrgIds(group: IRbacGroup): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  if (!enterpriseId) return;
  try {
    const resp: FetchSharedGroupOrgIdsResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/sharedGroups/${group?.groupId}`
      );
    return resp.data;
  } catch (error) {
    yield put(FetchSharedGroupOrgIds.ERROR(error as Error));
  }
}

export function* fetchSharedGroupOrgIds(): Generator {
  let assignedGroups = (yield select(getAssignedGroups)) as IRbacGroup[];

  if (!assignedGroups || assignedGroups.length === 0) {
    yield take(FetchAssignedGroups.Success.TYPE);
    assignedGroups = (yield select(getAssignedGroups)) as IRbacGroup[];
  }

  if (assignedGroups && assignedGroups.length > 0) {
    const result: IResponseItem[] | any = yield all(
      assignedGroups.map((group: IRbacGroup) =>
        call(getSharedGroupOrgIds, group)
      )
    );
    yield put(FetchSharedGroupOrgIds.SUCCESS(result));
  } else {
    yield put(FetchSharedGroupOrgIds.SUCCESS([] as any));
  }
}

// TODO: API call should be changed when we have single endpoint
export interface IGetAllGroupToRolesRelationsResp {
  data: {
    rbacGroupRoles: IGroupToRoleRelation[];
  };
}

export function* handleFetchGroupToRolesRelations(
  group: IRbacGroup
): Generator {
  const enterpriseId = yield select(getEnterpriseId);
  const userId = yield select(getActiveEditUserId);
  try {
    const response: IGetAllGroupToRolesRelationsResp | any =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/groups/${group.id}/roles`
      );
    return {
      response: response.data.rbacGroupRoles,
      success: true,
    };
  } catch (error) {
    const viewUserDetailsErrorName =
      usersErrorTelemetry.userRowActionMenu.viewUserDetailsError.name;
    const viewUserDetailsErrorProperties = {
      ...Object.fromEntries(
        usersErrorTelemetry.userRowActionMenu.viewUserDetailsError.properties ??
          new Map<UserTelemetryKeys, UserTelemetryValues>()
      ),
      [UserTelemetryKeys.TargetObjectId]: userId,
      [UserTelemetryKeys.ErrorEvent]: TRUE_VALUE,
      [UserTelemetryKeys.ErrorCode]:
        (error as any)?.response?.status ?? NETWORK_ERROR,
      [UserTelemetryKeys.ErrorReason]:
        error instanceof Error ? error.message : UNKNOWN_ERROR,
    };
    yield put(
      TelemetryErrorAction({
        name: viewUserDetailsErrorName,
        properties: viewUserDetailsErrorProperties,
      })
    );
    return {
      response: error as Error,
    };
  }
}

// Temporary solution until we do not have single endpoint for get all relations between selected user role and groups
export function* handleFetchGroupsToRolesRelations(): Generator {
  const allGroups = (yield select(getAllGroups)) as IRbacGroup[];
  const result: IResponseItem[] | any = yield all(
    allGroups.map((group: IRbacGroup) =>
      call(handleFetchGroupToRolesRelations, group)
    )
  );
  const groupsByRoles: IGroupToRoleRelation[] = getSuccessResponses(result);

  yield put(FetchGroupsToRolesRelations.SUCCESS(groupsByRoles));
}

export function* handleSetInviteDirty(
  action: IActionWithPayload<boolean>
): Generator {
  const { payload: isDirty } = action;

  yield delay(500);
  yield call(notifyFormPopulated, { isDirty });
}

export function* handleFetchAllProductsInstances(): Generator {
  const tableData = (yield select(
    getAssignedRolesWithInstancesTableData
  )) as IRbacAssignedRole[];
  const uniqueRegions: CDSOption[] = (yield select(
    getUniqueRegions
  )) as CDSOption[];
  if (uniqueRegions.length && tableData.length) {
    yield put(SetRegionFilterOptions(uniqueRegions));
  }
}

export function* fetchAllUserRolesAssignments(
  action: IActionWithPayload<string>
): Generator {
  const userId = action.payload;
  const enterpriseId = yield select(getEnterpriseId);

  try {
    const rolesResponse = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/rbac/users/${userId}/roles`
    );

    const groupsWithRolesResponse = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/rbac/users/${userId}/groupsWithRoleRelations`
    );

    const groupsWithRoles = (
      groupsWithRolesResponse as AxiosResponse<IGroupWithRoleRelationsResponse>
    ).data.rbacGroups;

    const assignedRoles = (
      rolesResponse as AxiosResponse<{
        rbacUserRolesAssignments: IRbacAssignedRole[];
      }>
    ).data.rbacUserRolesAssignments;

    yield put(
      FetchAllUserRolesAssignments.SUCCESS({
        groupsWithRoles,
        userId,
        assignedRoles,
      })
    );
  } catch (error: any) {
    yield put(FetchAllUserRolesAssignments.ERROR({ error, userId }));
  }
}

export function* resendUserInvitationEmail(
  action: IActionWithPayload<{ userId: string }>
): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;

  const { userId } = action.payload;

  try {
    yield prometheusAPIClient.post(
      `/api/enterprises/${enterpriseId}/users/${userId}/resendUserInvitationEmail`
    );
    yield put(ResendUserInvitationEmail.SUCCESS());
    yield put(FetchAllUsers.PENDING());
  } catch (error) {
    yield put(ResendUserInvitationEmail.ERROR(error as Error));
  }
}

export function* usersSaga(): Generator {
  yield takeEvery(UploadCSVFile.TYPE, uploadCSVFile);
  yield takeLatest(
    SetInviteStepperPendingToCreateUsers.TYPE,
    handleFinishInviteStepper
  );
  yield takeLatest(CreateAllUsers.Pending.TYPE, createAllUsers);
  // ----------------
  // TODO: temporary solution until we do not have endpoint which returns "Group - Roles" relations
  yield takeLatest(
    FetchGroupsToRolesRelations.Pending.TYPE,
    handleFetchGroupsToRolesRelations
  );
  // ----------------
  yield takeLatest(CreateGroup.Pending.TYPE, createGroup);
  yield takeEvery(
    FetchAllUserRolesAssignments.Pending.TYPE,
    fetchAllUserRolesAssignments
  );
  yield takeLatest(AssignSelectedGroups.Pending.TYPE, assignSelectedGroups);
  yield takeLatest(AssignSelectedGroup.Pending.TYPE, assignSelectedGroup);
  yield takeLatest(
    AssignSelectedGroupsForKPA.Pending.TYPE,
    assignSelectedGroupsForKPA
  );
  yield takeLatest(
    AssignSelectedGroupForKPA.Pending.TYPE,
    assignSelectedGroupForKPA
  );
  yield takeLatest(AddUserToAdminGroup.Pending.TYPE, addUserToAdminGroup);
  yield takeLatest(
    RemoveUserFromAdminGroup.Pending.TYPE,
    removeUserToAdminGroup
  );
  yield takeLatest(FetchAssignedRoles.Pending.TYPE, fetchAssignedRoles);
  yield takeLatest(AssignSelectedRoles.Pending.TYPE, assignSelectedRoles);
  yield takeLatest(AssignSelectedRole.Pending.TYPE, assignSelectedRole);
  yield takeLatest(
    AssignSelectedInstances.Pending.TYPE,
    assignSelectedInstances
  );
  yield takeLatest(AssignSelectedInstance.Pending.TYPE, assignSelectedInstance);
  yield takeLatest(
    AssignSelectedInstancesForKpaUser.Pending.TYPE,
    assignSelectedInstancesForKpaUser
  );
  yield takeLatest(
    AssignSelectedInstanceForKPA.Pending.TYPE,
    assignSelectedInstanceForKPA
  );
  yield takeLatest(DeleteAssignedRole.Pending.TYPE, deleteAssignedRole);
  yield takeLatest(DeleteAssignedGroup.Pending.TYPE, deleteAssignedGroup);
  yield takeLatest(FetchAssignedGroups.Pending.TYPE, fetchAssignedGroups);
  yield takeLatest(FetchAssignedGroups.Success.TYPE, fetchAssignedGroupsSucess);
  yield takeLatest(FETCH_PRODUCTS_LIST_SUCCESS, handleFetchProductsListSuccess);
  yield takeEvery(SetProductOptionsFromDrawer.TYPE, populateRolesSelect);
  yield takeLatest(FetchEditedUser.Pending.TYPE, fetchEditedUser);
  yield takeEvery(
    ShowEditUserDetailsDrawer.TYPE,
    handleShowEditUserDetailsDrawer
  );
  yield takeEvery(
    ToggleAssignRolesDrawerVisibility.TYPE,
    toggleAssignRolesDrawerVisibility
  );
  yield takeLatest(EditUserDetails.Pending.TYPE, editUserDetails);
  yield takeLatest(
    [FetchAllProductsInstances.Success.TYPE, FetchAssignedRoles.Success.TYPE],
    handleFetchAllProductsInstances
  );
  yield takeLatest(SetInviteDirty.TYPE, handleSetInviteDirty);
  yield takeLatest(FetchSharedUserOrgIds.Pending.TYPE, getSharedUserOrgIds);
  yield takeLatest(FetchSharedGroupOrgIds.Pending.TYPE, fetchSharedGroupOrgIds);
  yield takeLatest(PrepareGroupsOptions.TYPE, prepareGroupsOptions);
  yield takeLatest(
    ResendUserInvitationEmail.Pending.TYPE,
    resendUserInvitationEmail
  );
}
